import {Injectable} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';
import {
  DomainPageSettings,
  DomainStyleSettings,
} from '@gatewee/types/domain-setting';
import {PublicSettings} from '@gatewee/types/system';
import {tailwindLightTokens} from '@gatewee/constants';

@Injectable({providedIn: 'root'})
export class BrandingService {
  constructor(private title: Title, private meta: Meta) {}

  init(branding?: PublicSettings['branding']) {
    this.applyPageMetadata(branding?.page);
    this.applyStyle(branding?.style);
  }

  private applyPageMetadata(page: DomainPageSettings | undefined) {
    if (!page) return;

    this.title.setTitle(page.title);
    this.meta.addTags([
      {name: 'keywords', content: page.keywords},
      {name: 'description', content: page.description},
      {name: 'og:title', content: page.title},
      {name: 'og:description', content: page.description},
    ]);
  }

  private applyStyle(style: DomainStyleSettings | undefined) {
    const theme: 'light' | 'dark' = style?.theme === 'dark' ? 'dark' : 'light';
    this.applyThemeAttributes(theme);

    // Set brand primary color for new token system (must be in RGB triplet format for rgb(var()) to work).
    // Always set so palette.css and tokens have a valid rgb(var(--colors-brand-primary)); use default when API omits it.
    const primaryHex = this.normalizeHexColor(
      style?.primaryColor,
      tailwindLightTokens.brand.primary,
    );
    const rgbPrimary = this.hexToRgbTriplet(primaryHex);
    document.documentElement.style.setProperty(
      '--colors-brand-primary',
      rgbPrimary,
    );
    document.body.style.setProperty('--colors-brand-primary', rgbPrimary);

    if (style?.secondaryColor) {
      const secondaryHex = this.normalizeHexColor(
        style.secondaryColor,
        tailwindLightTokens.brand.primary,
      );
      const rgbSecondary = this.hexToRgbTriplet(secondaryHex);
      document.documentElement.style.setProperty(
        '--colors-brand-secondary',
        rgbSecondary,
      );
      document.body.style.setProperty('--colors-brand-secondary', rgbSecondary);
    }

    if (style?.accentColor) {
      const accentHex = this.normalizeHexColor(
        style.accentColor,
        tailwindLightTokens.brand.primary,
      );
      const rgbAccent = this.hexToRgbTriplet(accentHex);
      document.documentElement.style.setProperty(
        '--colors-brand-accent',
        rgbAccent,
      );
      document.body.style.setProperty('--colors-brand-accent', rgbAccent);
    }

    this.injectCustomCss(style?.customCss);

    // Apply favicon
    if (style?.favicon) {
      const link =
        document.querySelector("link[rel~='icon']") ||
        document.createElement('link');
      link.setAttribute('rel', 'icon');
      link.setAttribute('type', 'image/x-icon');
      link.setAttribute('href', style?.favicon ?? '');
      if (!link.parentNode) {
        document.head.appendChild(link);
      }
    }
  }

  private applyThemeAttributes(theme: 'light' | 'dark') {
    document.documentElement.setAttribute('data-theme-version', theme);
    document.body.setAttribute('data-theme-version', theme);

    const applyToTuiRoot = () => {
      document.querySelector('tui-root')?.setAttribute('tuiTheme', theme);
    };

    applyToTuiRoot();
    queueMicrotask(applyToTuiRoot);
    setTimeout(applyToTuiRoot, 0);
  }

  private normalizeHexColor(
    color: string | undefined,
    fallback: string,
  ): string {
    if (!color) return fallback;
    const normalized = color.trim();
    return /^#[0-9A-Fa-f]{6}$/.test(normalized) ? normalized : fallback;
  }

  /**
   * Converts hex color to RGB triplet format (e.g., "59, 130, 246")
   * This format is required for rgb(var(--colors-*)) to work in CSS
   */
  private hexToRgbTriplet(hex: string): string {
    // Remove # if present
    const cleanHex = hex.replace('#', '');

    // Parse RGB values
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);

    return `${r}, ${g}, ${b}`;
  }

  private injectCustomCss(css?: string) {
    const styleElementId = 'custom-css';
    const existing = document.getElementById(styleElementId);
    if (existing) existing.remove();

    if (css) {
      const style = document.createElement('style');
      style.id = styleElementId;
      style.innerText = css;
      document.head.appendChild(style);
    }
  }
}
